import { type AllergyRecord, allergyDisplayName, allergyReactions } from '@liza/fhir-utils';
import type { AllergyIntolerance } from '@medplum/fhirtypes';
import { format, parseISO } from 'date-fns';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '../../utils/date';
import { joinDescriptionParts, reactionsLine } from './allergyRowDescription';
import { type PromotionStatus, RecordListItem } from './RecordListItem';

export type AllergyRecordItemProps = {
  record: AllergyRecord;
  /** Rendered ahead of the reactions, e.g. the severity tier of the group the record heads. */
  lead?: React.ReactNode;
  onSelectAllergy?: (id: string) => void;
  selectedAllergyId?: string;
  onPromotionClick?: (id: string) => void;
  getPromotionStatus?: (id: string) => PromotionStatus;
};

const formatDay = (day: string): string => format(parseISO(day), DATE_FORMATS.DATE);

const datesLine = (record: AllergyIntolerance, t: TFunction<'records'>): string | undefined =>
  [
    record.recordedDate && t('recordedOnDate', { date: formatDay(record.recordedDate) }),
    record.onsetDateTime && t('onsetDateValue', { date: formatDay(record.onsetDateTime) }),
  ]
    .filter(Boolean)
    .join(' · ') || undefined;

/** One source record as a selectable, promotable row under its own recorded name. */
export const AllergyRecordItem = ({
  record,
  lead,
  onSelectAllergy,
  selectedAllergyId,
  onPromotionClick,
  getPromotionStatus,
}: AllergyRecordItemProps): React.ReactElement => {
  const { t } = useTranslation('records');
  return (
    <RecordListItem
      testId="allergy-item"
      title={allergyDisplayName(record) ?? t('unknownAllergy')}
      description={joinDescriptionParts([lead, reactionsLine(allergyReactions([record]), t), datesLine(record, t)])}
      onClick={onSelectAllergy ? (): void => onSelectAllergy(record.id) : undefined}
      selected={selectedAllergyId === record.id}
      onPromotionClick={onPromotionClick ? (): void => onPromotionClick(record.id) : undefined}
      promotionStatus={getPromotionStatus?.(record.id)}
    />
  );
};
